import { useCallback, useEffect, useState } from "react";
import Select from "react-select";
import HelpTooltip from "./HelpTooltip";
import { HELP } from "../utils/thorHelp";
import useFetchWithMsal from "../../../useFetchWithMsal";
import { thor_api_urls } from "../../common/Constants";
import { useThor } from "../context/ThorContext";
import SmallLoadingSpinner from "../../common/elements/SmallLoadingSpinner";
import { serverErrMsg } from "../utils/validators";

const toOption = (s) => ({ value: s.segmentId, label: s.segmentName });

const SegmentsTab = ({ experimentKey }) => {
    const { execute } = useFetchWithMsal();
    const { editMode, addToast } = useThor();

    const [allOptions, setAllOptions] = useState([]);
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const fetchData = useCallback(() => {
        setLoading(true);
        Promise.all([
            execute('GET', thor_api_urls.segments),
            execute('GET', thor_api_urls.experimentSegments(experimentKey)),
        ])
            .then(([allData, expData]) => {
                setAllOptions((allData?.segments ?? []).map(toOption));
                setSelected((expData?.segments ?? []).map(toOption));
            })
            .catch(err => {
                console.error("Failed to fetch segments:", err);
                addToast(serverErrMsg(err, 'Failed to load segments.'), 'danger');
            })
            .finally(() => setLoading(false));
    }, [execute, experimentKey, addToast]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const MAX_SEGMENTS = 3;

    const handleChange = (newSelection) => {
        if (!editMode || saving) return;

        const oldIds = new Set(selected.map(s => s.value));
        const newIds = new Set(newSelection.map(s => s.value));

        // Find the one item that changed
        const added = newSelection.find(s => !oldIds.has(s.value));
        const removed = selected.find(s => !newIds.has(s.value));

        if (added && newSelection.length > MAX_SEGMENTS) {
            addToast(`Maximum ${MAX_SEGMENTS} segments allowed.`, 'warning');
            return;
        }

        if (!added && !removed) return;

        // Capture previous value before optimistic update
        const prevSelected = selected;

        // Optimistically update UI
        setSelected(newSelection);
        setSaving(true);

        const request = added
            ? execute('PUT', thor_api_urls.experimentSegment(experimentKey, added.value))
            : execute('DELETE', thor_api_urls.experimentSegment(experimentKey, removed.value));

        request
            .then(() => addToast(added ? `Segment "${added.label}" added.` : `Segment "${removed.label}" removed.`, 'success'))
            .catch(err => {
                console.error("Failed to update segment:", err);
                addToast(serverErrMsg(err, 'Failed to update segment.'), 'danger');
                setSelected(prevSelected); // rollback to actual previous
            })
            .finally(() => setSaving(false));
    };

    if (loading) return <SmallLoadingSpinner />;

    return (
        <div>
            <div className="level mb-3">
                <div className="level-left">
                    <p className="subtitle is-5 mb-0">Segments</p>
                </div>
                {saving && (
                    <div className="level-right">
                        <span className="has-text-grey is-size-7">Saving…</span>
                    </div>
                )}
            </div>

            <p className="has-text-grey is-size-7 mb-4">
                Segments slice Heimdall metric results by user attributes (device type, sign-in status, locale, etc.). They do <strong>not</strong> restrict eligibility — all bucketed users still participate. Keep to 3 segments max to limit computation cost and false-positive risk.
            </p>

            <div className="field">
                <label className="label is-small">
                    Assigned Segments<HelpTooltip text={HELP.segments.general} />
                </label>
                <Select
                    isMulti
                    options={allOptions}
                    value={selected}
                    onChange={handleChange}
                    isDisabled={!editMode || saving}
                    isClearable={editMode}
                    placeholder="Select segments…"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
                <p className="help">
                    {editMode
                        ? 'Select or remove segments — each change saves immediately.'
                        : 'Enable Edit mode to modify segments.'}
                </p>
            </div>

        </div>
    );
};

export default SegmentsTab;
